import type { SubscriptionType } from '@form-factory/schema';
import { SubscriptionTier } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { Form, Input, type TypedFormState } from '@utima/ui-informed';
import { CircleAlert } from 'lucide-react';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { trpc, trpcUtils } from '@/services/trpc';

export interface OverrideLimitsFormProps {
  organizationId: string;
  currentSubscription:
    | SubscriptionType
    | Omit<SubscriptionType, 'organization'>;
}

type OverrideLimitsFormType = {
  maxUsers?: number;
  maxDocuments?: number;
};

export const OverrideLimitsForm = memo(function OverrideLimitsForm({
  organizationId,
  currentSubscription,
}: OverrideLimitsFormProps) {
  const { t } = useLingui();

  // As of now, the backend endpoint supports only FREE tier
  const disabled = currentSubscription?.tier !== SubscriptionTier.Free;

  const updateLimitsMutation =
    trpc.subscriptions.updateSubscriptionLimits.useMutation({
      onSuccess: () => {
        toast.success(t`Success`, {
          description: t`Successfully updated limits`,
        });
        trpcUtils.organizations.findOne.invalidate({ id: organizationId });
      },
      onError: error => {
        toast.error(t`Error`, {
          description:
            error.message || t`Failed to update limits. Please try again.`,
        });
      },
    });

  const handleSubmit = ({ values }: TypedFormState<OverrideLimitsFormType>) => {
    return updateLimitsMutation.mutate({
      organizationId,
      ...values,
    });
  };

  return (
    <Card className='my-6' title={<Trans>Override limits</Trans>}>
      {disabled && (
        <div className='flex flex-row gap-3 rounded-md border-2 border-secondary bg-slate-100 p-5'>
          <CircleAlert className='text-secondary' />
          <span className='text-sm text-muted-fg'>
            <Trans>
              Override is enabled only for Free tier subscription. Current
              subscription is {currentSubscription.tier}
            </Trans>
          </span>
        </div>
      )}
      <p className='text-sm text-muted-fg'>
        <Trans>
          Allows you to directly override limits related to the
          organization&apos;s subscription.
        </Trans>
      </p>
      <Form<OverrideLimitsFormType>
        onSubmit={handleSubmit}
        initialValues={currentSubscription}
      >
        <div className='flex flex-col gap-2'>
          <Input
            disabled={disabled}
            type='number'
            label={t`Maximum amount of users`}
            min={1}
            name='maxUsers'
          />
          <Input
            disabled={disabled}
            type='number'
            label={t`Maximum amount of source documents`}
            min={0}
            name='maxDocuments'
          />
          <Button
            data-cta='admin-override-limits-submit-button'
            disabled={disabled}
            type='submit'
            variant='primary'
          >
            <Trans>Override limits</Trans>
          </Button>
        </div>
      </Form>
    </Card>
  );
});
